import { Dialog, Fade, Grid, Slide } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { TransitionProps } from "@material-ui/core/transitions";
import React, { useEffect } from "react";
import { UIOverlayViewStatus } from "common-ts/dist/models/UIOverlayViewStatus";
import { BodyMeasurements, BodyMeasurementsForSize, CalculateClothBodyMeasurementsFromBodyMeasurements, ClothingType, FitType } from "common-ts/dist/models/UserMeasurements";
import { useAppDispatch, useAppSelector } from "../reduxhooks";
import { FlowComponentMaster } from "./OverlayViewOfflineFlowBlocks";
import OfflineMeasuringHeader from "./OverlayViewOfflineMeasuringHeader";
import OfflineVerticalProgress from "./OverlayViewOfflineFlowVerticalProgress";
import { OfflineActiveScreen, updateCurrentScreen } from "../redux/UIOverlayOfflineRedux";
import { openOverlay } from "../redux/UIOverlayOfflineRedux";
import { useHistory } from "react-router-dom";
import { RegularSizeValue } from "common-ts/dist/models/Cart";
import { checkAndRequestMicrophonePermission } from "../redux/MicrophoneRedux";
import { loadAllIframeVariablesFromLocalStorage } from "../redux/CartRedux";
import { fetchBranchPriceOverrides, getOfflineBranchId, getOfflineTrueStock } from "../redux/ProductsListRedux";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogContainer: {
            zIndex: (theme.zIndex.modal + 1 + " !important") as any,
        },
        dialogContentContainer: {
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "flex-start",
            flexDirection: "column",
            alignItems: "flex-start",
        },
        dialogContentLeftContainer: {
            width: "46%",
            paddingLeft: "29%",
            [theme.breakpoints.only("xs")]: {
                paddingLeft: "0%",
                width: "100%",
                justifyContent: "center",
            },
            display: "flex",
            justifyContent: "space-between",
        },
        titleLogoContainer: {
            width: "100%",
            position: "relative",
            paddingTop: "2.5vw",
            paddingBottom: "6vw",
            height: "5vw",
            [theme.breakpoints.only("xs")]: {
                paddingTop: "5vw",
                paddingBottom: "7vw",
                height: "10vw",
            },
            cursor: "pointer",
        },
        titleLogoDesktop: {
            paddingTop: "1.5vw",
            height: "5vw",
            [theme.breakpoints.only("xs")]: {
                paddingTop: "3vw",
                height: "10vw",
            },
        },
        titleLogoClose: {
            position: "absolute",
            top: "3vw",
            right: "3vw",
            color: "#000000",
        },
        signInsignUpToggleText: {
            fontSize: "15px",
            [theme.breakpoints.only("xs")]: {
                fontSize: "14px",
            },
        },
        horizontalBarDivider: {
            width: "100%",
            margin: "10px 0px 6px 0px",
            [theme.breakpoints.up("sm")]: {
                display: "none",
            },
        },
        horizontalContent: {
            width: "100%",
        },
    })
);

const Transition = React.forwardRef(function Transition(props: TransitionProps & { children?: React.ReactElement<any, any> }, ref: React.Ref<unknown>) {
    return <Slide direction="up" ref={ref} {...props} timeout={170} />;
});

export default function OverlayView() {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const overlayView = useAppSelector((state) => state.overlayOffline);
    const [firstName, setFirstName] = React.useState<string>('');
    const microphoneState = useAppSelector((state) => state.microphoneState);

    const [childrenVisible, setChildrenVisible] = React.useState<boolean>(true);
    const [titleNumber, setTitleNumber] = React.useState<number>(-1);
    const [currentBodyMeasurements, setCurrentBodyMeasurements] = React.useState<BodyMeasurements>(
        CalculateClothBodyMeasurementsFromBodyMeasurements(ClothingType.Body, FitType.RegularFit, BodyMeasurementsForSize["Medium (M)"], RegularSizeValue.M, [])
    );
    const [currentRegularSizeValue, setCurrentRegularSizeValue] = React.useState<RegularSizeValue>(RegularSizeValue.M);
    const [currentClothingType, setCurrentClothingType] = React.useState<ClothingType>(ClothingType.KemejaPanjang);
    const [manualOverride, setManualOverride] = React.useState(false);
    const [firstIn, setFirstIn] = React.useState<boolean>(true);
    const [offlineBranchId, setOfflineBranchId] = React.useState<string | null>(null);
    const [offlineTrueStock, setOfflineTrueStock] = React.useState<boolean>(false);

    useEffect(() => {
        dispatch(
            openOverlay()
        );
    }, [dispatch]);

    useEffect(() => {
        // Pin this device to an offline branch once via ?setBranchId=STORE-JKT, then load
        // that branch's price overrides so offline prices reflect the branch.
        const setBranchId = new URLSearchParams(window.location.search).get('setBranchId');
        if (setBranchId) {
            try {
                localStorage.setItem('offlineBranchId', setBranchId);
            } catch {}
        }
        // Hidden true-stock toggle: ?setTrueStock=1 pins this device to availability
        // excluding hutang stok (material still on the way); ?setTrueStock=0 clears it.
        const setTrueStock = new URLSearchParams(window.location.search).get('setTrueStock');
        if (setTrueStock !== null) {
            try {
                if (setTrueStock === '1') localStorage.setItem('offlineTrueStock', '1');
                else localStorage.removeItem('offlineTrueStock');
            } catch {}
        }
        setOfflineBranchId(getOfflineBranchId());
        setOfflineTrueStock(getOfflineTrueStock());
        dispatch(fetchBranchPriceOverrides());
    }, [dispatch]);

    function delayedDispatch(currentScreen: OfflineActiveScreen) {
        setChildrenVisible(false);
        setTimeout(() => {
            dispatch(updateCurrentScreen(currentScreen));
            setChildrenVisible(true);
        }, 1000);
    }

    function closeOverlayAndResetUserMeasurementDraft() {
        delayedDispatch(OfflineActiveScreen.StandardBrowsing); // BACK TO STANDARD BROWSING
    }

    let content: JSX.Element = FlowComponentMaster(
        {
            firstName: firstName,
            microphoneState: microphoneState,
        },
        {
            currentScreen: overlayView.currentScreen,
            titleNumber: titleNumber,
            currentBodyMeasurements: currentBodyMeasurements,
            currentClothingType: currentClothingType,
            currentRegularSizeValue: currentRegularSizeValue,
            manualOverride: manualOverride,
            firstIn: firstIn
        },
        {
            delayedDispatch,
            setTitleNumber,
            setCurrentBodyMeasurements,
            setCurrentClothingType,
            setCurrentRegularSizeValue,
            setManualOverride: setManualOverride,
            setFirstIn: setFirstIn,
            setFirstName: setFirstName,
            requestMicPermission: () => dispatch(checkAndRequestMicrophonePermission()),
            loadCartFromLocalStorage: () => dispatch(loadAllIframeVariablesFromLocalStorage())
        }
    );

    // Show the floating "currently measuring" header on every screen between
    // SizePreset and SizeResult (inclusive), except SizeSelect — that stage shows
    // the card + name in its own top bar instead. Rendered outside the Fade below
    // so it stays put (doesn't fade) when the screen changes.
    const showMeasuringHeader = [
        OfflineActiveScreen.SizePreset,
        OfflineActiveScreen.BenefitMiddle,
        OfflineActiveScreen.SizeLoading,
        OfflineActiveScreen.SizeResult,
    ].includes(overlayView.currentScreen);

    return (
        <Dialog
            fullScreen
            maxWidth="lg"
            open={overlayView.status === UIOverlayViewStatus.On}
            TransitionComponent={Transition}
            // keepMounted
            onClose={closeOverlayAndResetUserMeasurementDraft}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            className={classes.dialogContainer}
        >
            <div className={classes.dialogContentContainer}>
                <Grid container>
                    <Grid item xs={2}>
                        <OfflineVerticalProgress currentIndex={titleNumber} onClose={closeOverlayAndResetUserMeasurementDraft} />
                    </Grid>
                    <Grid item xs={10}>
                        {showMeasuringHeader ? <OfflineMeasuringHeader firstName={firstName} /> : null}
                        <Fade in={overlayView.status === UIOverlayViewStatus.On && childrenVisible} timeout={400}>
                            <div className={classes.horizontalContent}>
                                {content}
                            </div>
                        </Fade>
                    </Grid>
                </Grid>
            </div>
            {offlineBranchId || offlineTrueStock ? (
                <div
                    style={{
                        position: "fixed",
                        bottom: "8px",
                        left: "10px",
                        fontSize: "12px",
                        color: "rgba(0,0,0,0.45)",
                        pointerEvents: "none",
                        zIndex: 2147483647,
                    }}
                >
                    {[offlineBranchId, offlineTrueStock ? "Stok Fisik" : null].filter(Boolean).join(" · ")}
                </div>
            ) : null}
        </Dialog>
    );
}
